import { useEffect, useRef, useState, useCallback } from "react";
import { endpoints } from "@/lib/api";
import { Pencil, Eraser, Trash2, Type } from "lucide-react";
import { toast } from "sonner";

// Warm palette that matches the wood-panel theme
const COLORS = [
  { name: "cream", value: "#EAE4DB" }, // default — chalk-like against dark wood
  { name: "amber", value: "#D6A85B" },
  { name: "sage", value: "#8DA88B" },
  { name: "clay", value: "#D07A65" },
];

// Fixed canvas resolution — we scale via CSS to fit the panel.
// Storing at a fixed resolution keeps saves consistent across devices.
const CANVAS_W = 900;
const CANVAS_H = 1200;
const TEXT_FONT_PX = 44; // rendered in canvas coords; scales down to a comfortable read size
const TEXT_FONT = `${TEXT_FONT_PX}px 'Fraunces', 'Playfair Display', Georgia, serif`;

export default function NotepadPanel() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const saveTimerRef = useRef(null);
  const loadedRef = useRef(false);

  const [tool, setTool] = useState("pen"); // pen | eraser | text
  const [color, setColor] = useState(COLORS[0].value);
  const [size] = useState(4);
  const [saving, setSaving] = useState(false);

  // Text-input state — when the user taps in text mode, we show a floating <input>
  // anchored at the tap location. On commit, the text is painted onto the canvas.
  const [textInput, setTextInput] = useState(null); // { canvasPt: {x,y}, screenPt: {x,y}, value: "" }
  const textInputRef = useRef(null);

  // Convert a pointer event to canvas-space coords.
  const eventToCanvasCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * CANVAS_W;
    const y = ((e.clientY - rect.top) / rect.height) * CANVAS_H;
    return { x, y };
  }, []);

  const drawLine = useCallback((from, to, opts) => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.save();
    if (opts.tool === "eraser") {
      ctx.globalCompositeOperation = "destination-out";
      ctx.lineWidth = size * 6;
      ctx.strokeStyle = "rgba(0,0,0,1)";
    } else {
      ctx.globalCompositeOperation = "source-over";
      ctx.lineWidth = size;
      ctx.strokeStyle = opts.color;
    }
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.restore();
  }, [size]);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      try {
        setSaving(true);
        const data = canvas.toDataURL("image/png");
        await endpoints.saveNotepad({ data });
      } catch (err) {
        console.error("Notepad save failed", err);
        toast.error("Could not save notepad");
      } finally {
        setSaving(false);
      }
    }, 600);
  }, []);

  // Pointer handlers — pointer events cover mouse, pen, and touch uniformly.
  const onPointerDown = (e) => {
    // Text mode: single tap opens a floating input; don't start a stroke.
    if (tool === "text") {
      e.preventDefault();
      const pt = eventToCanvasCoords(e);
      if (!pt) return;
      const canvasRect = canvasRef.current.getBoundingClientRect();
      // Anchor the input at the tap position (in viewport coords) so the user
      // sees the caret exactly where the text will land.
      const scale = canvasRect.width / CANVAS_W;
      setTextInput({
        canvasPt: pt,
        left: canvasRect.left + pt.x * scale,
        top: canvasRect.top + pt.y * scale - TEXT_FONT_PX * scale * 0.85,
        scale,
        value: "",
      });
      // Focus the input on the next frame (after mount)
      setTimeout(() => textInputRef.current?.focus(), 0);
      return;
    }
    e.preventDefault();
    const pt = eventToCanvasCoords(e);
    if (!pt) return;
    drawingRef.current = true;
    lastPtRef.current = pt;
    // Draw a tiny dot so single-tap leaves a mark
    drawLine(pt, { x: pt.x + 0.1, y: pt.y + 0.1 }, { tool, color });
    canvasRef.current?.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    const pt = eventToCanvasCoords(e);
    if (!pt) return;
    drawLine(lastPtRef.current, pt, { tool, color });
    lastPtRef.current = pt;
  };

  const onPointerUp = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPtRef.current = null;
    try { canvasRef.current?.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
    scheduleSave();
  };

  const clearCanvas = () => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    scheduleSave();
  };

  const commitText = useCallback(() => {
    const t = textInput;
    if (!t) return;
    const trimmed = t.value.trim();
    if (trimmed) {
      const ctx = canvasRef.current?.getContext("2d");
      if (ctx) {
        ctx.save();
        ctx.font = TEXT_FONT;
        ctx.textBaseline = "top";
        ctx.fillStyle = color;
        ctx.fillText(trimmed, t.canvasPt.x, t.canvasPt.y);
        ctx.restore();
        scheduleSave();
      }
    }
    setTextInput(null);
  }, [textInput, color, scheduleSave]);

  const cancelText = () => setTextInput(null);

  // Load persisted image on mount
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const res = await endpoints.getNotepad();
        if (!res?.data) return;
        const img = new Image();
        img.onload = () => {
          const ctx = canvasRef.current?.getContext("2d");
          if (!ctx) return;
          ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
          ctx.drawImage(img, 0, 0, CANVAS_W, CANVAS_H);
        };
        img.src = res.data;
      } catch (err) {
        console.error("Notepad load failed", err);
      }
    })();
  }, []);

  return (
    <div
      data-testid="notepad-card"
      className="wood-panel rounded-[2rem] border p-5 lg:p-6 flex flex-col overflow-hidden flex-1 min-h-0"
    >
      <div className="flex items-center gap-2.5 mb-3">
        <Pencil size={20} className="text-[#D6A85B]" />
        <h2 className="font-serif text-xl md:text-2xl tracking-tight text-[#EAE4DB]">
          Notes
        </h2>
        <span
          data-testid="notepad-saving-indicator"
          className={`ml-auto text-[0.6rem] font-mono uppercase tracking-[0.15em] transition ${
            saving ? "text-[#D6A85B] opacity-100" : "text-[#A6A098] opacity-0"
          }`}
        >
          saving…
        </span>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3">
        <button
          data-testid="notepad-tool-pen"
          onClick={() => setTool("pen")}
          className={`h-9 w-9 rounded-full border flex items-center justify-center transition ${
            tool === "pen" ? "border-[#D6A85B] bg-[#D6A85B]/15" : "border-[#33302C] hover:border-[#5a544e]"
          }`}
          aria-label="Pen tool"
        >
          <Pencil size={14} className="text-[#EAE4DB]" />
        </button>
        <button
          data-testid="notepad-tool-eraser"
          onClick={() => setTool("eraser")}
          className={`h-9 w-9 rounded-full border flex items-center justify-center transition ${
            tool === "eraser" ? "border-[#D6A85B] bg-[#D6A85B]/15" : "border-[#33302C] hover:border-[#5a544e]"
          }`}
          aria-label="Eraser tool"
        >
          <Eraser size={14} className="text-[#EAE4DB]" />
        </button>
        <button
          data-testid="notepad-tool-text"
          onClick={() => setTool("text")}
          className={`h-9 w-9 rounded-full border flex items-center justify-center transition ${
            tool === "text" ? "border-[#D6A85B] bg-[#D6A85B]/15" : "border-[#33302C] hover:border-[#5a544e]"
          }`}
          aria-label="Text tool"
        >
          <Type size={14} className="text-[#EAE4DB]" />
        </button>

        <div className="mx-1 w-px h-6 bg-[#33302C]" />

        {COLORS.map((c) => (
          <button
            key={c.name}
            data-testid={`notepad-color-${c.name}`}
            onClick={() => {
              setColor(c.value);
              setTool("pen");
            }}
            className="h-8 w-8 rounded-full border-2 transition"
            style={{
              backgroundColor: c.value,
              borderColor: color === c.value && tool === "pen" ? "#EAE4DB" : "#33302C",
              transform: color === c.value && tool === "pen" ? "scale(1.1)" : "scale(1)",
            }}
            aria-label={`Color ${c.name}`}
          />
        ))}

        <button
          data-testid="notepad-clear"
          onClick={clearCanvas}
          className="ml-auto h-9 px-3 rounded-full border border-[#33302C] hover:border-[#C85A5A] hover:text-[#E89090] transition flex items-center gap-1.5"
          aria-label="Clear canvas"
        >
          <Trash2 size={13} className="text-current" />
          <span className="text-[0.65rem] font-mono uppercase tracking-[0.2em]">clear</span>
        </button>
      </div>

      {/* Canvas — dark parchment surface so cream/amber ink stands out */}
      <div
        ref={wrapRef}
        className="flex-1 min-h-0 rounded-2xl overflow-hidden border border-[#33302C]"
      >
        <canvas
          ref={canvasRef}
          data-testid="notepad-canvas"
          width={CANVAS_W}
          height={CANVAS_H}
          className={`block w-full h-full touch-none ${tool === "text" ? "cursor-text" : "cursor-crosshair"}`}
          style={{
            background:
              "radial-gradient(circle at 30% 20%, #2a2622 0%, #1c1a18 70%)",
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={onPointerUp}
        />
      </div>

      {/* Floating text input — appears at the tap position when text tool is active */}
      {textInput && (
        <div
          data-testid="notepad-text-input-overlay"
          className="fixed z-[250]"
          style={{
            left: `${textInput.left}px`,
            top: `${textInput.top}px`,
          }}
        >
          <input
            ref={textInputRef}
            data-testid="notepad-text-input"
            value={textInput.value}
            onChange={(e) => setTextInput((t) => ({ ...t, value: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commitText(); }
              if (e.key === "Escape") { e.preventDefault(); cancelText(); }
            }}
            onBlur={commitText}
            placeholder="Type…"
            className="bg-transparent border-b-2 outline-none px-1 min-w-[8rem]"
            style={{
              color,
              borderColor: color,
              fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif",
              // Match canvas rendering size so the caret shows where the text will land.
              fontSize: `${TEXT_FONT_PX * textInput.scale}px`,
              lineHeight: 1,
              caretColor: color,
              textShadow: "0 1px 2px rgba(0,0,0,0.6)",
            }}
          />
          <div className="mt-1 text-[0.6rem] font-mono uppercase tracking-[0.2em] text-[#A6A098]">
            enter to commit · esc to cancel
          </div>
        </div>
      )}
    </div>
  );
}
